import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";
import PortalSidebar from "../PortalSidebar/PortalSidebar";

const Pagination = ({ page, total, perPage, onChange }) => {
  const first = total === 0 ? 0 : (page - 1) * perPage + 1;
  const last = Math.min(page * perPage, total);
  const pages = Math.max(1, Math.ceil(total / perPage));

  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        Previous
      </button>

      <span className={styles.pageMeta}>
        {total === 0 ? "0 - 0 of 0" : `${first}-${last} of ${total}`}
      </span>

      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
      >
        Next
      </button>
    </div>
  );
};

export default function RentalUnitViewPage() {
  const navigate = useNavigate();
  const { rentalUnitId } = useParams();

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingAccess, setLoadingAccess] = useState(true);
  const [userAccess, setUserAccess] = useState([]);

  const [rentalUnit, setRentalUnit] = useState(null);
  const [agreements, setAgreements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [activeTab, setActiveTab] = useState("Rental Agreements");
  const tabs = ["Rental Agreements"];

  const [agreementPage, setAgreementPage] = useState(1);
  const agreementsPerPage = 10;

  const formatDateSafe = (rawDate) => {
    if (!rawDate) return "—";
    try {
      return format(new Date(rawDate), "dd MMM yyyy");
    } catch {
      return rawDate;
    }
  };

  const formatCurrencySafe = (value) => {
    if (value === null || value === undefined || value === "") return "R0.00";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `R${num.toLocaleString("en-ZA", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const displayValue = (obj, key) => {
    return (
      obj?.[`${key}@OData.Community.Display.V1.FormattedValue`] ??
      obj?.[key] ??
      "—"
    );
  };

  useEffect(() => {
    document.title = "GMA Portal || Rental Unit";

    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      if (desktop) setSidebarOpen(true);
      else setSidebarOpen(false);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    function handleClick(e) {
      const sidebar = document.getElementsByClassName(styles.sidebar)[0];
      const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

      if (!sidebar || !hamburger) return;

      const clickedInsideSidebar = sidebar.contains(e.target);
      const clickedHamburger = hamburger.contains(e.target);
      const isDesktop = window.innerWidth >= 1024;

      if (
        !isDesktop &&
        sidebarOpen &&
        !clickedInsideSidebar &&
        !clickedHamburger
      ) {
        setSidebarOpen(false);
      }
    }

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, [sidebarOpen]);

  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
              ? name
              : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          },
        );

        const data = await resp.json();

        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);

  useEffect(() => {
    const fetchRentalUnitDetail = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const resp = await fetch(
          `https://portal.gmacc.co.za/api/rental/units/${rentalUnitId}/`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        const data = await resp.json();

        if (!resp.ok) {
          throw new Error(data.error || "Failed to load rental unit.");
        }

        setRentalUnit(data.rental_unit || null);
        setAgreements(data.rental_agreements || []);
        document.title = `${data.rental_unit?.cj_name || "Rental Unit"} • Rental Unit Detail`;
      } catch (err) {
        console.error(err);
        setError(err.message || "Failed to load rental unit.");
      } finally {
        setLoading(false);
      }
    };

    fetchRentalUnitDetail();
  }, [navigate, rentalUnitId]);

  const paginatedAgreements = useMemo(() => {
    const start = (agreementPage - 1) * agreementsPerPage;
    return agreements.slice(start, start + agreementsPerPage);
  }, [agreements, agreementPage]);


  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("contact_id");
    localStorage.removeItem("rental_portal_mode");
    navigate("/#/");
  };

  const summaryFields = [
    ["Unit Name", "cj_name"],
    ["Size", "cj_size"],
    ["Occupation Status", "cj_occupationstatus"],
  ];

  const unitInfoFields = [
    ["Rental Unit", "cj_name"],
    ["Size", "cj_size"],
    ["Occupation Status", "cj_occupationstatus"],
    ["Landlord Account", "_cj_landlordaccount_value"],
    ["Created On", "createdon"],
    ["Modified On", "modifiedon"],
  ];

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            {rentalUnit?.cj_name || "Rental Unit Detail"}
          </h1>
        </div>

        <div
          className={styles.userChip}
          onClick={() => navigate("/user-profile")}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      <div className={styles.shell}>
        <PortalSidebar
  activePortal="rental"
  activeTab="landlord"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  hiddenTabKeys={[
    ...(!userAccess.includes("landlord") ? ["landlord"] : []),
    ...(!userAccess.includes("tenant") ? ["tenant"] : []),
  ]}
  ticketRole={null}
  onLogout={handleLogout}
/>

        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {error ? (
              <p className={styles.errorText}>{error}</p>
            ) : (
              <>
                <div className={styles.paymentHeaderCard}>
                  <button
                    className={styles.paymentBackBtn}
                    onClick={() => navigate(-1)}
                  >
                    Back
                  </button>

                  <h1 className={styles.paymentHeaderTitle}>RENTAL UNIT</h1>

                  <div className={styles.paymentHeaderDivider} />

                  <div className={styles.paymentHeaderGrid3}>
                    {summaryFields.map(([label]) => (
                      <div
                        key={label}
                        className={styles.paymentHeaderLabelItem}
                      >
                        {label}:
                      </div>
                    ))}
                  </div>

                  <div className={styles.paymentHeaderValuePill}>
                    <div className={styles.paymentHeaderGrid3}>
                      {summaryFields.map(([_, key]) => (
                        <div
                          key={key}
                          className={styles.paymentHeaderValueItem}
                        >
                          {key.toLowerCase().includes("date")
                            ? formatDateSafe(rentalUnit?.[key])
                            : displayValue(rentalUnit, key)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className={styles.subTabs}>
                  {tabs.map((t) => (
                    <div
                      key={t}
                      className={
                        activeTab === t ? styles.subTabActive : styles.subTab
                      }
                      onClick={() => {
                        setActiveTab(t);
                        setAgreementPage(1);
                      }}
                    >
                      {t}
                    </div>
                  ))}
                </div>

                <div className={styles.paymentCardGrid}>
                  {activeTab === "Rental Agreements" && (
                    <div className={styles.paymentCard}>
                      <h2>Rental Agreements</h2>

                      {agreements.length === 0 ? (
                        <p className={styles.infoText}>
                          No rental agreements found for this rental unit.
                        </p>
                      ) : (
                        <>
                          <div className={styles.responsiveTableWrapper}>
                            <table className={styles.dataTable}>
                              <thead>
                                <tr>
                                  <th>Agreement Name</th>
                                  <th>Start Date</th>
                                  <th>Agreement Term</th>
                                  <th>Landlord Account</th>
                                  <th>Tenant Account</th>
                                  <th>Monthly Rental</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginatedAgreements.map((item) => (
                                  <tr
                                    key={item.cj_rentalagreementid}
                                    onClick={() =>
                                      navigate(
                                        `/rental-agreement/${item.cj_rentalagreementid}`,
                                      )
                                    }
                                    style={{ cursor: "pointer" }}
                                  >
                                    <td className={styles.paymentNumberCell}>
                                      {item.cj_name || ""}
                                    </td>
                                    <td>{formatDateSafe(item.cj_startdate)}</td>
                                    <td>{item.cj_agreementterm || ""}</td>
                                    <td>{item.lessor_account_name || ""}</td>
                                    <td>{item.lessee_account_name || ""}</td>
                                    <td>
                                      {formatCurrencySafe(
                                        item.cj_monthlyrental,
                                      )}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          <Pagination
                            page={agreementPage}
                            total={agreements.length}
                            perPage={agreementsPerPage}
                            onChange={setAgreementPage}
                          />
                        </>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
